// 画像まわりの確認と、カードの画像の並び（最大3枚。先頭が表紙）の組み立て。
// 画像は、運営が用意したプリセット（全員）か、自分の写真（Pro のみ）。
import type { FutureImage, FutureImageSelection } from "../../api/futureBoard";

export const MAX_IMAGES_PER_CARD = 3;
export const MAX_UPLOAD_BYTES = 1024 * 1024;
export const ALLOWED_IMAGE_TYPES: readonly string[] = ["image/jpeg", "image/png", "image/webp"];

export type ImageFileProblem = "too_large" | "unsupported";

// 選んだ時点で分かる問題。判断の最終はサーバー（形式はファイルの中身で判定する）。
export function checkImageFile(file: { size?: number | null; type?: string | null }): ImageFileProblem | null {
  if (typeof file.size === "number" && file.size > MAX_UPLOAD_BYTES) return "too_large";
  if (file.type && !ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) return "unsupported";
  return null;
}

export const canAddImage = (images: readonly unknown[]): boolean => images.length < MAX_IMAGES_PER_CARD;

// 画面に出している画像から、サーバーへ送る並びを作る（position の順）。
export function selectionFromImages(images: readonly FutureImage[]): FutureImageSelection[] {
  return [...images]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((image): FutureImageSelection => (image.type === "preset" && image.preset_key
      ? { type: "preset", preset_key: image.preset_key }
      : { type: "upload", image_id: image.id }));
}

function sameSelection(a: FutureImageSelection, b: FutureImageSelection): boolean {
  if (a.type === "preset" && b.type === "preset") return a.preset_key === b.preset_key;
  if (a.type === "upload" && b.type === "upload") return a.image_id === b.image_id;
  return false;
}

// 追加。上限（3枚）を超える追加と、同じ画像の重複は無視する。
export function addSelection(selection: readonly FutureImageSelection[], item: FutureImageSelection): FutureImageSelection[] {
  if (selection.length >= MAX_IMAGES_PER_CARD || selection.some(existing => sameSelection(existing, item))) return [...selection];
  return [...selection, item];
}

export function removeSelection(selection: readonly FutureImageSelection[], index: number): FutureImageSelection[] {
  return selection.filter((_, position) => position !== index);
}

// 表紙にする（指定の画像を先頭へ）。
export function makeCover(selection: readonly FutureImageSelection[], index: number): FutureImageSelection[] {
  if (index <= 0 || index >= selection.length) return [...selection];
  return [selection[index], ...selection.filter((_, position) => position !== index)];
}

export const sameSelections = (a: readonly FutureImageSelection[], b: readonly FutureImageSelection[]): boolean =>
  a.length === b.length && a.every((item, index) => sameSelection(item, b[index]));

// 画像の説明。本人が付けたものを優先し、なければプリセットの説明（英語表示なら英語）。どちらもなければ、
// 内容を推測せず、既定の言葉にする（futureBoard.image.defaultAlt =「未来カードの画像」）。
export function altTextFor(
  image: Pick<FutureImage, "alt_text" | "type">,
  locale: string,
  defaultAlt: string,
  presetAltEn?: string,
): string {
  const own = image.alt_text?.trim();
  if (own) return own;
  if (image.type === "preset" && locale.startsWith("en") && presetAltEn) return presetAltEn;
  return defaultAlt;
}

// 一覧の切り抜き位置（0〜100）を、CSS の object-position に使う値にする。
export const focalPosition = (image: Pick<FutureImage, "focal_x" | "focal_y">): string => `${image.focal_x}% ${image.focal_y}%`;
